import axios from '@axios'
import moment from 'moment'
import { storePagination } from '@/mixins'

/**
 * Módulo Vuex da tela dedicada de "Configuração de Delivery" (D28/D29, tarefa 16.1).
 *
 * Consome os endpoints administrativos:
 *  - Loja:  GET/PUT  /api/stores/{storeId}/delivery-config
 *  - Geral: GET/PUT  /api/store-delivery-config/general (StoreId nulo)
 *
 * A taxa vive exclusivamente nas faixas (D3): não há taxa escalar. Gratuidade é `value = 0`
 * (D4). O "Até" (`productsValueTo`) das faixas é derivado e somente leitura, vindo do backend
 * (D2). Não há seletor de canal (só existe `B2C`, D6) nem monotonicidade (D8).
 */

// Ordem dos sete dias, com o valor acompanhando o enum DayOfWeek do backend (string).
const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Faixa de taxa padrão de uma config nova (0 → 0,00): taxa base grátis (D3/D4).
const getDefaultTier = () => ({
  tierId: null,
  productsValueFrom: 0,
  productsValueTo: null,
  value: 0,
})

// Um dia sem override: todos os campos nulos herdam o padrão da config (D6) e a lista de
// faixas vazia herda as faixas padrão (D9).
const getEmptyDay = dayOfWeek => ({
  dayOfWeek,
  deliveryEnabled: null,
  deliveryStartTime: null,
  deliveryEndTime: null,
  deliveryFeePerKilometer: null,
  minOrderValue: null,
  tiers: [],
})

// Garante exatamente sete dias (dom → sáb) na ordem canônica, mesclando os dias vindos do
// backend com os dias vazios que faltarem para a grade ficar sempre completa.
const buildWeek = (days = []) => {
  const byDay = new Map((days || []).map(day => [day.dayOfWeek, day]))
  return WEEK_DAYS.map(dayOfWeek => {
    const existing = byDay.get(dayOfWeek)
    if (!existing) return getEmptyDay(dayOfWeek)
    return {
      ...getEmptyDay(dayOfWeek),
      ...existing,
      dayOfWeek,
      tiers: (existing.tiers || []).map(tier => ({ ...tier })),
    }
  })
}

// Config base nova (Priority 0, vigente sempre), com uma faixa padrão 0 → 0,00.
const getNewConfig = () => ({
  id: null,
  salesChannel: 'B2C',
  priority: 0,
  effectiveFrom: null,
  effectiveTo: null,
  active: true,
  isCampaign: false,
  defaultDeliveryEnabled: true,
  defaultDeliveryFeePerKilometer: null,
  defaultMinOrderValue: null,
  defaultTiers: [getDefaultTier()],
  days: buildWeek(),
  excludedGroupIds: [],
})

// A vigência do backend é semiaberta (D25): EffectiveFrom inclusivo e EffectiveTo EXCLUSIVO.
// O datepicker do "Vigente até" só dá a data (meia-noite), e o usuário lê o campo como o ÚLTIMO
// dia da campanha — então a gravação leva o fim do dia. Sem isso, uma campanha de um único dia
// (de = até = 07/09) mandava um intervalo vazio e o backend recusava com
// INVALID_EFFECTIVE_PERIOD. Fim do dia mantém a data no mesmo dia, então a listagem e o
// datepicker continuam exibindo o dia escolhido sem conversão na leitura.
const toEndOfDay = value => (value ? moment(value).endOf('day').format() : null)

// Normaliza uma config vinda do backend para o formato editável (sete dias completos).
const normalizeConfig = config => ({
  ...getNewConfig(),
  ...config,
  salesChannel: config.salesChannel || 'B2C',
  // Config gravada como "por KM" (antes de a loja rodar os dois modelos) pode vir sem faixas: semeia
  // a faixa base 0 → 0,00, obrigatória para salvar (I4).
  defaultTiers: config.defaultTiers?.length
    ? config.defaultTiers.map(tier => ({ ...tier }))
    : [getDefaultTier()],
  days: buildWeek(config.days),
  // Os ids vão como string para casar com as opções do combo (o vue-select do Formulate
  // normaliza os values das opções para string); o backend aceita string em campo int.
  excludedGroupIds: (config.excludedGroupIds || []).map(id => id.toString()),
})

// Monta o corpo de gravação (upsert) a partir da config editada. O "Até" derivado
// (`productsValueTo`) não trafega na escrita (D2); só limiar e valor.
const toUpsertPayload = config => ({
  id: config.id || null,
  salesChannel: config.salesChannel || 'B2C',
  priority: config.priority || 0,
  effectiveFrom: config.effectiveFrom || null,
  // O "até" da tela é o último dia da campanha: vai como fim desse dia (limite exclusivo, D25).
  effectiveTo: toEndOfDay(config.effectiveTo),
  active: config.active,
  defaultDeliveryEnabled: config.defaultDeliveryEnabled,
  defaultDeliveryFeePerKilometer: config.defaultDeliveryFeePerKilometer,
  defaultMinOrderValue: config.defaultMinOrderValue,
  defaultTiers: (config.defaultTiers || []).map(tier => ({
    productsValueFrom: tier.productsValueFrom,
    value: tier.value,
  })),
  days: (config.days || []).map(day => ({
    dayOfWeek: day.dayOfWeek,
    deliveryEnabled: day.deliveryEnabled,
    deliveryStartTime: day.deliveryStartTime || null,
    deliveryEndTime: day.deliveryEndTime || null,
    deliveryFeePerKilometer: day.deliveryFeePerKilometer,
    minOrderValue: day.minOrderValue,
    tiers: (day.tiers || []).map(tier => ({
      productsValueFrom: tier.productsValueFrom,
      value: tier.value,
    })),
  })),
  excludedGroupIds: [...(config.excludedGroupIds || [])],
})

const getInitialState = () => ({
  // Paginação/ordenação padrão do sistema para a listagem de lojas com config própria.
  ...storePagination.state(),
  // Escopo em edição (D29): 'general' (StoreId nulo) ou 'store' (uma loja).
  scope: 'store',
  selectedStoreId: null,
  stores: [],
  // Grupos de produto (para o seletor de grupos excluídos da base de cálculo — D15).
  groups: [],
  // Resumo da config Geral (fixa no topo da listagem) e das lojas com config própria (paginadas).
  generalSummary: null,
  scopeSummary: [],
  // Configs carregadas do escopo (base + campanhas).
  configs: [],
  // Config atualmente em edição.
  config: getNewConfig(),
  // Quando falso (padrão), o backend esconde campanhas expiradas na listagem (Req. 24.10).
  includeExpired: false,
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    storeOptions(state) {
      return state.stores.map(store => ({
        value: store.id,
        label: store.tradingName || store.companyName || `#${store.id}`,
      }))
    },

    // Config base (Priority 0) do escopo carregado, se houver.
    baseConfig(state) {
      return state.configs.find(config => (config.priority || 0) === 0) || null
    },

    // Verdadeiro quando a loja selecionada tem configuração PRÓPRIA salva (base ou campanhas).
    // Quando falso no escopo de loja, a loja resolve pela configuração Geral (D29).
    hasOwnConfig(state) {
      return state.scope === 'store' && state.configs.length > 0
    },

    // Campanhas (Priority > 0) do escopo carregado.
    campaignConfigs(state) {
      return state.configs.filter(config => (config.priority || 0) > 0)
    },

    // Opções de grupos de produto para o seletor de grupos excluídos (D15).
    groupOptions(state) {
      return state.groups.map(group => ({ value: group.id, label: group.name }))
    },

    // Faixas efetivas de um dia: as próprias do dia, ou as padrão da config quando vazias (D9).
    effectiveTiersForDay: state => dayOfWeek => {
      const day = state.config.days.find(d => d.dayOfWeek === dayOfWeek)
      if (day && day.tiers && day.tiers.length > 0) return day.tiers
      return state.config.defaultTiers
    },

    // Verdadeiro quando o dia não tem faixas próprias e, portanto, herda as padrão (D9).
    isDayInheritingTiers: state => dayOfWeek => {
      const day = state.config.days.find(d => d.dayOfWeek === dayOfWeek)
      return !day || !day.tiers || day.tiers.length === 0
    },
  },

  mutations: {
    ...storePagination.mutations,

    SET_SCOPE(state, scope) {
      state.scope = scope
    },
    SET_SELECTED_STORE(state, storeId) {
      state.selectedStoreId = storeId
    },
    SET_STORES(state, stores) {
      state.stores = stores
    },
    SET_CONFIGS(state, configs) {
      state.configs = configs
    },
    SET_CONFIG(state, config) {
      state.config = config
    },
    SET_INCLUDE_EXPIRED(state, includeExpired) {
      state.includeExpired = includeExpired
    },
    SET_SCOPE_SUMMARY(state, summary) {
      state.scopeSummary = summary
    },
    SET_GENERAL_SUMMARY(state, general) {
      state.generalSummary = general
    },
    SET_GROUPS(state, groups) {
      state.groups = groups
    },
  },

  actions: {
    ...storePagination.actions,

    setScope({ commit }, scope) {
      commit('SET_SCOPE', scope)
    },

    setSelectedStore({ commit }, storeId) {
      commit('SET_SELECTED_STORE', storeId)
    },

    setIncludeExpired({ commit }, includeExpired) {
      commit('SET_INCLUDE_EXPIRED', includeExpired)
    },

    async fetchStores({ commit }) {
      const { results } = (
        await axios.get('/api/stores', { params: { pageSize: 0, pageIndex: 0 } })
      ).data
      commit('SET_STORES', results)
    },

    // Carrega os grupos de produto para o seletor de grupos excluídos da base de cálculo (D15).
    async fetchGroups({ commit }) {
      const { results } = (
        await axios.get('/api/product-groups', { params: { pageSize: 0, pageIndex: 0 } })
      ).data
      commit('SET_GROUPS', results || [])
    },

    // Carrega o resumo dos escopos para a listagem: a Geral (fixa no topo) e as lojas com config
    // própria PAGINADAS. O filtro por loja e a paginação vão para o servidor (padrão do sistema).
    async fetchScopeSummary({ commit, state }, { storeId = null } = {}) {
      const { data } = await axios.get('/api/store-delivery-config/summary', {
        params: {
          storeId: storeId || null,
          pageSize: state.paging.pageSize,
          pageIndex: state.paging.currentPage - 1,
        },
      })
      commit('SET_GENERAL_SUMMARY', data.general || null)
      commit('SET_SCOPE_SUMMARY', (data.stores && data.stores.results) || [])
      commit('SET_PAGING', {
        ...state.paging,
        rowCount: (data.stores && data.stores.rowCount) || 0,
        rowsInCurrentPage: (data.stores && data.stores.rowsInCurrentPage) || 0,
      })
    },

    // As listas de config passaram a ser paginadas (padrão do sistema). A tela edita o conjunto
    // inteiro (base + campanhas), então pedimos pageSize: 0 (traz tudo) e lemos `results`.
    // includeExpired controla se campanhas encerradas entram (padrão: escondidas — Req. 24.10).
    async fetchDeliveryConfig({ commit, state }, { storeId }) {
      const { results } = (
        await axios.get(`/api/stores/${storeId}/delivery-config`, {
          params: { pageSize: 0, pageIndex: 0, includeExpired: state.includeExpired },
        })
      ).data
      commit('SET_CONFIGS', results || [])
    },

    async fetchGeneralDeliveryConfig({ commit, state }) {
      const { results } = (
        await axios.get('/api/store-delivery-config/general', {
          params: { pageSize: 0, pageIndex: 0, includeExpired: state.includeExpired },
        })
      ).data
      commit('SET_CONFIGS', results || [])
    },

    async saveDeliveryConfig({ state }, { storeId }) {
      const { data } = await axios.put(
        `/api/stores/${storeId}/delivery-config`,
        toUpsertPayload(state.config)
      )
      return data
    },

    async saveGeneralDeliveryConfig({ state }) {
      const { data } = await axios.put(
        '/api/store-delivery-config/general',
        toUpsertPayload(state.config)
      )
      return data
    },

    // Exclui TODAS as configs próprias da loja (base + campanhas) para que ela volte a resolver
    // pela configuração Geral (D29). Cada config é removida pelo endpoint DELETE por id.
    async deleteOwnConfig({ state }, { storeId }) {
      const ids = state.configs.map(config => config.id).filter(id => !!id)
      await Promise.all(
        ids.map(id => axios.delete(`/api/stores/${storeId}/delivery-config/${id}`))
      )
    },

    // Seleciona uma config carregada para edição (por id) ou inicia uma config base nova.
    selectConfig({ commit, state }, configId) {
      if (configId === null || configId === undefined) {
        commit('SET_CONFIG', getNewConfig())
        return
      }
      const found = state.configs.find(config => config.id === configId)
      commit('SET_CONFIG', found ? normalizeConfig(found) : getNewConfig())
    },

    // Inicia uma nova campanha (Priority >= 1, vigência obrigatória — I14 validado no servidor)
    // a partir da configuração BASE do escopo, como ponto de partida: o usuário só define o período
    // de vigência e ajusta o que muda no período. Zera o id (registro novo) e as datas de vigência.
    startNewCampaign({ commit, state }) {
      const base = state.configs.find(config => (config.priority || 0) === 0)
      const source = base ? normalizeConfig(base) : getNewConfig()
      // Prioridade única: uma acima da maior já existente no escopo (prioridades não se repetem).
      const maxPriority = state.configs.reduce((max, config) => Math.max(max, config.priority || 0), 0)
      commit('SET_CONFIG', {
        ...source,
        id: null,
        priority: maxPriority + 1,
        isCampaign: true,
        active: true,
        effectiveFrom: null,
        effectiveTo: null,
        defaultTiers: (source.defaultTiers || []).map(tier => ({ ...tier, tierId: null })),
        days: (source.days || []).map(day => ({
          ...day,
          tiers: (day.tiers || []).map(tier => ({ ...tier, tierId: null })),
        })),
        excludedGroupIds: [...(source.excludedGroupIds || [])],
      })
    },

    // Exclui UMA configuração por id no escopo atual (loja ou Geral) — usada para excluir campanhas.
    async deleteConfig({ state }, { configId }) {
      if (state.scope === 'store') {
        await axios.delete(`/api/stores/${state.selectedStoreId}/delivery-config/${configId}`)
      } else {
        await axios.delete(`/api/store-delivery-config/general/${configId}`)
      }
    },

    clearConfig({ commit }) {
      commit('SET_CONFIG', getNewConfig())
      commit('SET_CONFIGS', [])
    },
  },
}
